import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HighnoteTransactionEvent } from 'API';
import { agaveService } from 'app/services/agave.service';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAccountType } from 'app/hooks/useAccountType';

/**
 * SEE-1154: resolve the *project-scoped* cost-code name for coded transactions.
 *
 * Sage Intacct cost codes (TASK) are project-scoped — the same code (and the
 * same 1:1 `AgaveCostCode` id) has a different name per project. A coded
 * transaction stores only `agaveCostCodeId` + `agaveProjectId`; the display
 * resolves `agaveCostCode` from the id alone (Amplify `@hasOne`), so it always
 * renders the single collapsed name regardless of project. The per-project name
 * lives on the `AgaveProjectCostCode` join and is surfaced by the coding
 * resolver only when a `projectId` is supplied.
 *
 * This hook fetches, for the distinct projects of the currently-rendered
 * transactions, that resolver's project-scoped cost-code names — using the
 * service directly so it never touches the shared coding Redux slice the
 * dropdowns rely on. It returns a lookup keyed by `(projectId, costCodeId)`.
 *
 * Strictly additive: a caller overlays the returned name only when one exists;
 * when the lookup returns `undefined` (non-Agave paidol, no join row, fetch not
 * yet complete, or fetch failed) the existing collapsed name is left untouched.
 */
export type PerProjectCostCodeNameLookup = (
  projectId?: string | null,
  costCodeId?: string | null
) => string | undefined;

/**
 * Channel for the resolver so the shared cost-code dropdown (CodingEntityDropdown)
 * can overlay the project-scoped label without fetching per instance. A surface
 * that renders coding for known transactions (table, sidesheet, bulk coding
 * sheet) calls the hook once and provides the result here. Defaults to a no-op
 * so any dropdown rendered outside a provider is simply unaffected.
 */
export const PerProjectCostCodeNameContext = createContext<PerProjectCostCodeNameLookup>(() => undefined);

// projectId -> (costCodeId -> project-scoped name)
type NameMap = Map<string, Map<string, string>>;

// One page is enough: per-paidol cost-code counts are bounded and the project
// path stays on DynamoDB (no OpenSearch), so a large limit returns every code
// for the project in a single call.
const PROJECT_COST_CODE_FETCH_LIMIT = 1000;

export function usePerProjectCostCodeNames(
  transactions: Array<HighnoteTransactionEvent>
): PerProjectCostCodeNameLookup {
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { hasErpCodingUI, isConstructionType } = useAccountType();
  const useAgave = hasErpCodingUI && isConstructionType;
  const paidolId = selectedPaidol?.id;

  const [nameMap, setNameMap] = useState<NameMap>(() => new Map());
  // Projects we've already fetched (or are fetching) so we never refetch.
  const fetchedProjects = useRef<Set<string>>(new Set());

  // Distinct projects among transactions that carry both a project and a cost
  // code — the only rows whose label could be project-scoped. Sorted+joined so
  // the effect key is stable across re-renders that don't change the set.
  const projectIdsKey = useMemo(() => {
    if (!useAgave) return '';
    const set = new Set<string>();
    for (const tx of transactions) {
      if (tx.agaveProjectId && tx.agaveCostCodeId) set.add(tx.agaveProjectId);
    }
    return Array.from(set).sort().join(',');
  }, [transactions, useAgave]);

  useEffect(() => {
    if (!useAgave || !paidolId || !projectIdsKey) return;

    const toFetch = projectIdsKey.split(',').filter((pid) => pid && !fetchedProjects.current.has(pid));
    if (toFetch.length === 0) return;

    let cancelled = false;
    // Mark as in-flight up front so a re-render can't kick off duplicate fetches.
    toFetch.forEach((pid) => fetchedProjects.current.add(pid));

    void Promise.all(
      toFetch.map(async (projectId) => {
        try {
          const result = await agaveService.v2SearchAgaveCostCodes({
            input: { filter: { paidolId, projectId }, limit: PROJECT_COST_CODE_FETCH_LIMIT },
          });
          if (cancelled) return;
          const items = result.items ?? [];
          if (items.length === PROJECT_COST_CODE_FETCH_LIMIT) {
            // Hit the page cap — some codes for this project weren't fetched, so their
            // per-project name won't overlay. Log it so a truncation is diagnosable.
            // eslint-disable-next-line no-console
            console.debug(
              `[SEE-1154] usePerProjectCostCodeNames truncated at ${PROJECT_COST_CODE_FETCH_LIMIT} for project ${projectId}`
            );
          }
          const codeMap = new Map<string, string>();
          for (const item of items) {
            if (item?.id && typeof item.name === 'string' && item.name) {
              codeMap.set(item.id, item.name);
            }
          }
          setNameMap((prev) => {
            const next = new Map(prev);
            next.set(projectId, codeMap);
            return next;
          });
        } catch {
          // Additive/fail-soft: leave existing labels untouched and allow a
          // later render to retry this project.
          fetchedProjects.current.delete(projectId);
        }
      })
    );

    return () => {
      cancelled = true;
    };
  }, [projectIdsKey, paidolId, useAgave]);

  return useCallback(
    (projectId?: string | null, costCodeId?: string | null) => {
      if (!projectId || !costCodeId) return undefined;
      return nameMap.get(projectId)?.get(costCodeId);
    },
    [nameMap]
  );
}
